import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Tooltip,
  Flex,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const DidacticsPedagogyContentManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    category: "",
    content_type: "TIP",
    title: "",
    tip_details: "",
    short_description: "",
    video_url: "",
    voice_url: "",
    thumbnail_url: "",
    duration: "",
    status: "ACTIVE",
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  // File states for immediate upload
  const [videoFile, setVideoFile] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  // Pagination and search states
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [contentTypeFilter, setContentTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [contentList, setContentList] = useState([]);

  // Category and Content Type options
  const categoryOptions = [
    {
      value: "CREATING_TEAM_CULTURE",
      label: t("didacticsManager.categoryCreatingTeamCulture"),
    },
    {
      value: "UNMOTIVATED_PLAYERS",
      label: t("didacticsManager.categoryUnmotivatedPlayers"),
    },
    {
      value: "UNDESIRABLE_BEHAVIOR",
      label: t("didacticsManager.categoryUndesirableBehavior"),
    },
    {
      value: "DIFFERENCES_SKILL_LEVEL",
      label: t("didacticsManager.categoryDifferencesSkillLevel"),
    },
    {
      value: "MATCH_GUIDANCE_COACHING",
      label: t("didacticsManager.categoryMatchGuidanceCoaching"),
    },
  ];

  const contentTypeOptions = [
    { value: "TIP", label: t("didacticsManager.typeTip") },
    { value: "VIDEO", label: t("didacticsManager.typeVideo") },
    { value: "VOICE_OVER", label: t("didacticsManager.typeVoiceOver") },
  ];

  // Status filter options
  const statusOptions = [
    { value: "ACTIVE", label: t("common.active") },
    { value: "INACTIVE", label: t("common.inactive") },
  ];

  // API hooks
  const {
    execute: fetchList,
    error: fetchError,
    loading: loadingList,
  } = useGet(
    `${API_ENDPOINTS.DIDACTICS_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&content_type=${contentTypeFilter || ""}&category=${categoryFilter || ""}&status=${statusFilter || ""}`,
  );
  const {
    post,
    error: postError,
    loading: creating,
  } = usePost(API_ENDPOINTS.DIDACTICS_CREATE);
  const {
    execute: getById,
    error: getByIdError,
    loading: loadingById,
  } = useDynamicApi("GET");
  const {
    execute: putData,
    error: putError,
    loading: updating,
  } = useDynamicApi("PUT");
  const {
    execute: patchData,
    error: patchError,
    loading: updatingStatus,
  } = useDynamicApi("PATCH");
  const {
    execute: deleteData,
    error: deleteError,
    loading: deleting,
  } = useDynamicApi("DELETE");

  // Loading state
  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    submitting;

  // Load data with current pagination and search
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setContentList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize]);

  // Handle search and filters
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, contentTypeFilter, categoryFilter, statusFilter]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Reset dependent fields when content type changes
    if (name === "content_type") {
      if (value === "TIP") {
        setVideoFile(null);
        setVoiceFile(null);
        setThumbnailFile(null);
        setFormData((prev) => ({
          ...prev,
          video_url: "",
          voice_url: "",
          thumbnail_url: "",
          duration: "",
          short_description: "",
        }));
      } else if (value === "VIDEO") {
        setVoiceFile(null);
        setFormData((prev) => ({
          ...prev,
          tip_details: "",
          voice_url: "",
        }));
      } else if (value === "VOICE_OVER") {
        setVideoFile(null);
        setThumbnailFile(null);
        setFormData((prev) => ({
          ...prev,
          tip_details: "",
          video_url: "",
          thumbnail_url: "",
        }));
      }
    }
  };

  // Upload file to UPLOAD_MEDIA API
  const uploadFile = async (file, fileType) => {
    if (!file) return null;

    if (fileType === "video") setUploadingVideo(true);
    if (fileType === "voice") setUploadingVoice(true);
    if (fileType === "thumbnail") setUploadingThumbnail(true);

    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("adminToken") ||
        sessionStorage.getItem("access_token");

      if (!token) {
        Toast.error(t("common.error"));
        return null;
      }

      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.UPLOAD_MEDIA}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (response.ok && result.image_url) {
        return result.image_url;
      } else {
        const errorMessage =
          result.message || result.detail || t("didacticsManager.failedUpload");
        Toast.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      Toast.error(error.message || t("didacticsManager.failedUpload"));
      return null;
    } finally {
      if (fileType === "video") setUploadingVideo(false);
      if (fileType === "voice") setUploadingVoice(false);
      if (fileType === "thumbnail") setUploadingThumbnail(false);
    }
  };

  // Handle video file selection
  const handleVideoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validVideoTypes = [
      "video/mp4",
      "video/mov",
      "video/avi",
      "video/mkv",
      "video/webm",
    ];
    if (!validVideoTypes.includes(file.type)) {
      Toast.error(t("didacticsManager.invalidVideoType"));
      e.target.value = "";
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      Toast.error(t("didacticsManager.fileSizeExceededVideo"));
      e.target.value = "";
      return;
    }

    setVideoFile(file);
    Toast.info(t("didacticsManager.uploadingVideo"));

    const videoUrl = await uploadFile(file, "video");

    if (videoUrl) {
      setFormData((prev) => ({
        ...prev,
        video_url: videoUrl,
      }));
      Toast.success(t("didacticsManager.uploadComplete"));
    } else {
      setVideoFile(null);
      e.target.value = "";
    }
  };

  // Handle voice file selection
  const handleVoiceSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validAudioTypes = [
      "audio/mpeg",
      "audio/mp3",
      "audio/wav",
      "audio/ogg",
      "audio/m4a",
    ];
    if (!validAudioTypes.includes(file.type)) {
      Toast.error(t("didacticsManager.invalidAudioType"));
      e.target.value = "";
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      Toast.error(t("didacticsManager.fileSizeExceededAudio"));
      e.target.value = "";
      return;
    }

    setVoiceFile(file);
    Toast.info(t("didacticsManager.uploadingAudio"));

    const voiceUrl = await uploadFile(file, "voice");

    if (voiceUrl) {
      setFormData((prev) => ({
        ...prev,
        voice_url: voiceUrl,
      }));
      Toast.success(t("didacticsManager.uploadComplete"));
    } else {
      setVoiceFile(null);
      e.target.value = "";
    }
  };

  // Handle thumbnail file selection
  const handleThumbnailSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validImageTypes.includes(file.type)) {
      Toast.error(t("didacticsManager.invalidImageType"));
      e.target.value = "";
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      Toast.error(t("didacticsManager.fileSizeExceededImage"));
      e.target.value = "";
      return;
    }

    setThumbnailFile(file);
    Toast.info(t("didacticsManager.uploadingThumbnail"));

    const thumbnailUrl = await uploadFile(file, "thumbnail");

    if (thumbnailUrl) {
      setFormData((prev) => ({
        ...prev,
        thumbnail_url: thumbnailUrl,
      }));
      Toast.success(t("didacticsManager.uploadComplete"));
    } else {
      setThumbnailFile(null);
      e.target.value = "";
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      category: "",
      content_type: "TIP",
      title: "",
      tip_details: "",
      short_description: "",
      video_url: "",
      voice_url: "",
      thumbnail_url: "",
      duration: "",
      status: "ACTIVE",
    });
    setFormErrors({});
    setVideoFile(null);
    setVoiceFile(null);
    setThumbnailFile(null);
    setSelectedContent(null);
    setIsEditMode(false);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setContentTypeFilter("");
    setCategoryFilter("");
    setStatusFilter("");
  };

  // Validate form function - Only sets errors, no toast
  const validateForm = () => {
    const errors = {};

    // Category validation
    if (!formData.category) {
      errors.category = t("didacticsManager.categoryRequired");
    }

    // Title validation
    if (!formData.title.trim()) {
      errors.title = t("didacticsManager.titleRequired");
    } else if (formData.title.trim().length < 2) {
      errors.title = t("didacticsManager.titleMinLength");
    } else if (formData.title.trim().length > 200) {
      errors.title = t("didacticsManager.titleMaxLength");
    }

    // Content type specific validation
    if (formData.content_type === "TIP") {
      if (!formData.tip_details.trim()) {
        errors.tip_details = t("didacticsManager.tipDetailsRequired");
      } else if (formData.tip_details.trim().length < 10) {
        errors.tip_details = t("didacticsManager.tipDetailsMinLength");
      }
    }

    if (formData.content_type === "VIDEO") {
      if (!formData.video_url && !videoFile) {
        errors.video_url = t("didacticsManager.videoRequired");
      }
      if (!formData.short_description.trim()) {
        errors.short_description = t("didacticsManager.descriptionRequired");
      }
    }

    if (formData.content_type === "VOICE_OVER") {
      if (!formData.voice_url && !voiceFile) {
        errors.voice_url = t("didacticsManager.audioRequired");
      }
      if (!formData.short_description.trim()) {
        errors.short_description = t("didacticsManager.descriptionRequired");
      }
    }

    // Duration validation for video and voice-over
    if (
      (formData.content_type === "VIDEO" ||
        formData.content_type === "VOICE_OVER") &&
      formData.duration
    ) {
      const durationRegex = /^(\d{1,2}:)?[0-5]?\d:[0-5]\d$/;
      if (!durationRegex.test(formData.duration)) {
        errors.duration = t("didacticsManager.invalidDuration");
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle API errors
  const handleApiError = (error) => {
    if (error?.response?.data?.errors) {
      setFormErrors(error.response.data.errors);
    } else if (error?.response?.data?.message) {
      Toast.error(error.response.data.message);
    } else {
      Toast.error(error?.message || t("common.error"));
    }
  };

  // Handle create content
  const handleCreate = async (e) => {
    e.preventDefault();

    // Only validate, no toast for validation errors
    if (!validateForm()) {
      return; // Errors show below fields, no toast
    }

    // Check if files were selected but upload might have failed
    if (videoFile && !formData.video_url) {
      Toast.error(t("didacticsManager.uploadWait"));
      return;
    }
    if (voiceFile && !formData.voice_url) {
      Toast.error(t("didacticsManager.uploadWait"));
      return;
    }
    if (thumbnailFile && !formData.thumbnail_url) {
      Toast.error(t("didacticsManager.uploadWait"));
      return;
    }

    setSubmitting(true);

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("didacticsManager.createdSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("didacticsManager.failedCreate"));
        }
      }
    } catch (error) {
      console.error("Error creating content:", error);
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle update content
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedContent?.id) {
      Toast.error(t("didacticsManager.idNotFound"));
      return;
    }

    // Only validate, no toast for validation errors
    if (!validateForm()) {
      return; // Errors show below fields, no toast
    }

    setSubmitting(true);

    try {
      const endpoint = `${API_ENDPOINTS.DIDACTICS_UPDATE}/${selectedContent.id}`;
      const response = await putData(endpoint, formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("didacticsManager.updatedSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("didacticsManager.failedUpdate"));
        }
      }
    } catch (error) {
      console.error("Error updating content:", error);
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (item) => {
    try {
      const newStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const endpoint = `${API_ENDPOINTS.DIDACTICS_STATUS_CHANGE}/${item.id}`;

      const response = await patchData(endpoint, {
        status: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            (newStatus === "ACTIVE"
              ? t("common.active")
              : t("common.inactive")),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("attackManager.failedUpdateStatus"));
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error(error.message || t("attackManager.failedUpdateStatus"));
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (item) => {
    setContentToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!contentToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.DIDACTICS_DELETE}/${contentToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(
          response?.message || t("didacticsManager.deletedSuccess"),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("didacticsManager.failedDelete"));
      }
    } catch (error) {
      console.error("Error deleting content:", error);
      Toast.error(error.message || t("didacticsManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setContentToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  // Helper function to fetch content by ID
  const fetchContentById = async (id) => {
    const endpoint = `${API_ENDPOINTS.DIDACTICS_DETAIL_BY_ID}/${id}`;
    const response = await getById(endpoint);
    return response;
  };

  // Open modal for edit
  const openEditModal = async (item) => {
    try {
      const response = await fetchContentById(item.id);

      if (response?.status === true && response.data) {
        setSelectedContent(response.data);
        setFormData({
          category: response.data.category || "",
          content_type: response.data.content_type || "TIP",
          title: response.data.title || "",
          tip_details: response.data.tip_details || "",
          short_description: response.data.short_description || "",
          video_url: response.data.video_url || "",
          voice_url: response.data.voice_url || "",
          thumbnail_url: response.data.thumbnail_url || "",
          duration: response.data.duration || "",
          status: response.data.status || "ACTIVE",
        });
        setVideoFile(null);
        setVoiceFile(null);
        setThumbnailFile(null);
        setFormErrors({});
        setIsEditMode(true);
        setIsModalOpen(true);
      } else {
        Toast.error(t("didacticsManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching content details:", error);
      Toast.error(error.message || t("didacticsManager.failedFetchDetails"));
    }
  };

  // Navigate to view page
  const openViewPage = (id) => {
    navigate(`/didactics/details/${id}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get display name for category
  const getCategoryDisplay = (category) => {
    const found = categoryOptions.find((opt) => opt.value === category);
    return found ? found.label : category;
  };

  // Get display name for content type
  const getContentTypeDisplay = (type) => {
    const found = contentTypeOptions.find((opt) => opt.value === type);
    return found ? found.label : type;
  };

  // Check if any upload is in progress
  const isUploading = uploadingVideo || uploadingVoice || uploadingThumbnail;

  const columns = [
    {
      title: t("didacticsManager.title"),
      dataIndex: "title",
      key: "title",
      render: (titleValue) => titleValue || "N/A",
    },
    {
      title: t("didacticsManager.category"),
      dataIndex: "category",
      key: "category",
      render: (categoryValue) => getCategoryDisplay(categoryValue),
    },
    {
      title: t("didacticsManager.type"),
      dataIndex: "content_type",
      key: "content_type",
      render: (typeValue) => (
        <Tag
          color={
            typeValue === "TIP"
              ? "blue"
              : typeValue === "VIDEO"
                ? "green"
                : "purple"
          }
        >
          {getContentTypeDisplay(typeValue)}
        </Tag>
      ),
    },
    {
      title: t("common.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (dateValue) => formatDate(dateValue),
    },
    {
      title: t("common.status"),
      dataIndex: "status",
      key: "status",
      render: (statusValue) => (
        <Tag color={statusValue === "ACTIVE" ? "green" : "default"}>
          {statusValue === "ACTIVE" ? t("common.active") : t("common.inactive")}
        </Tag>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip
            title={
              record.status === "ACTIVE"
                ? t("common.inactive")
                : t("common.active")
            }
          >
            <Button
              className="btn-toggle-active"
              size="small"
              danger={record.status === "ACTIVE"}
              type={record.status === "ACTIVE" ? "default" : "primary"}
              icon={<PoweroffOutlined />}
              onClick={() => handleToggleStatus(record)}
              disabled={updatingStatus}
            />
          </Tooltip>
          <Tooltip title={t("common.view")}>
            <Button
              className="btn-view"
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <Button
              className="btn-edit"
              type="primary"
              size="small"
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <Button
              className="btn-delete"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const hasFilters =
    searchTerm || contentTypeFilter || categoryFilter || statusFilter;

  return (
    <>
      <Card
        className="didactics-manager-card"
        title={t("didacticsManager.title")}
        extra={
          <Button type="primary" onClick={openCreateModal} disabled={loading}>
            {t("didacticsManager.createNew")}
          </Button>
        }
      >
        <Flex gap="middle" wrap="wrap" style={{ marginBottom: 20 }}>
          <Input
            placeholder={t("didacticsManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            style={{ width: 280 }}
            disabled={loading}
          />
          <Select
            placeholder={t("didacticsManager.type")}
            value={contentTypeFilter || undefined}
            onChange={(v) => setContentTypeFilter(v || "")}
            style={{ width: 160 }}
            allowClear
            options={contentTypeOptions}
            disabled={loading}
          />
          <Select
            placeholder={t("didacticsManager.category")}
            value={categoryFilter || undefined}
            onChange={(v) => setCategoryFilter(v || "")}
            style={{ width: 220 }}
            allowClear
            options={categoryOptions}
            disabled={loading}
          />
          <Select
            placeholder={t("common.filterByStatus")}
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v || "")}
            style={{ width: 140 }}
            allowClear
            options={statusOptions}
            disabled={loading}
          />
          {hasFilters && (
            <Button onClick={resetFilters} disabled={loading}>
              {t("common.clearFilters")}
            </Button>
          )}
        </Flex>

        <div className="table-responsive">
          <Table
            dataSource={contentList}
            columns={columns}
            rowKey="id"
            loading={loading}
            size="middle"
            locale={{
              emptyText: hasFilters
                ? t("didacticsManager.noMatchingContent")
                : t("didacticsManager.noContentFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${t("common.showing")} ${(currentPage - 1) * pageSize + 1} ${t("common.to")} ${Math.min(
                  currentPage * pageSize,
                  total,
                )} ${t("common.of")} ${total} ${t("common.entries")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? t("didacticsManager.editTitle")
              : t("didacticsManager.createTitle")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? submitting
                ? t("didacticsManager.updating")
                : t("common.update")
              : submitting
                ? t("didacticsManager.creating")
                : t("common.create")
          }
          showConfirm={true}
          size="large"
          confirmDisabled={submitting || isUploading}
        >
          <form onSubmit={isEditMode ? handleUpdate : handleCreate}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                >
                  {t("didacticsManager.category")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  disabled={submitting}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: formErrors.category
                      ? "1px solid #dc3545"
                      : "1px solid #d9d9d9",
                    borderRadius: "4px",
                  }}
                >
                  <option value="">
                    {t("didacticsManager.selectCategory")}
                  </option>
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formErrors.category && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {formErrors.category}
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                >
                  {t("didacticsManager.type")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="content_type"
                  value={formData.content_type}
                  onChange={handleInputChange}
                  disabled={submitting}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                  }}
                >
                  {contentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                {t("didacticsManager.title")}{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t("didacticsManager.enterTitle")}
                disabled={submitting}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: formErrors.title
                    ? "1px solid #dc3545"
                    : "1px solid #d9d9d9",
                  borderRadius: "4px",
                }}
                maxLength={200}
              />
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  textAlign: "right",
                  marginTop: "4px",
                }}
              >
                {formData.title.length}/200 {t("common.characters")}
              </div>
              {formErrors.title && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {formErrors.title}
                </div>
              )}
            </div>

            {/* Tip Content */}
            {formData.content_type === "TIP" && (
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                >
                  {t("didacticsManager.tipDetails")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <textarea
                  name="tip_details"
                  value={formData.tip_details}
                  onChange={handleInputChange}
                  placeholder={t("didacticsManager.enterTipDetails")}
                  disabled={submitting}
                  rows="6"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: formErrors.tip_details
                      ? "1px solid #dc3545"
                      : "1px solid #d9d9d9",
                    borderRadius: "4px",
                  }}
                />
                {formErrors.tip_details && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {formErrors.tip_details}
                  </div>
                )}
              </div>
            )}

            {/* Video Content */}
            {formData.content_type === "VIDEO" && (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    {t("didacticsManager.description")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    placeholder={t("didacticsManager.enterDescription")}
                    disabled={submitting}
                    rows="4"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: formErrors.short_description
                        ? "1px solid #dc3545"
                        : "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  />
                  {formErrors.short_description && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formErrors.short_description}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    {t("didacticsManager.uploadVideo")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    style={{
                      border: "1px dashed #d9d9d9",
                      padding: "16px",
                      borderRadius: "4px",
                    }}
                  >
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoSelect}
                      disabled={submitting || uploadingVideo}
                    />
                    {videoFile && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "13px",
                          color: "#666",
                        }}
                      >
                        {videoFile.name} (
                        {(videoFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </div>
                    )}
                    {uploadingVideo && (
                      <div style={{ marginTop: "8px", color: "#1890ff" }}>
                        {t("didacticsManager.uploading")}
                      </div>
                    )}
                    {formData.video_url && !uploadingVideo && (
                      <div style={{ marginTop: "8px", color: "#52c41a" }}>
                        ✓ {t("didacticsManager.uploaded")}
                      </div>
                    )}
                  </div>
                  {formErrors.video_url && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formErrors.video_url}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                      }}
                    >
                      {t("didacticsManager.duration")}
                    </label>
                    <input
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder={t("didacticsManager.durationPlaceholder")}
                      disabled={submitting}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: formErrors.duration
                          ? "1px solid #dc3545"
                          : "1px solid #d9d9d9",
                        borderRadius: "4px",
                      }}
                    />
                    {formErrors.duration && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {formErrors.duration}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                      }}
                    >
                      {t("didacticsManager.thumbnail")}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailSelect}
                      disabled={submitting || uploadingThumbnail}
                    />
                    {thumbnailFile && (
                      <div
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          color: "#666",
                        }}
                      >
                        {thumbnailFile.name} (
                        {(thumbnailFile.size / 1024).toFixed(2)} KB)
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Voice Over Content */}
            {formData.content_type === "VOICE_OVER" && (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    {t("didacticsManager.description")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    placeholder={t(
                      "didacticsManager.enterVoiceOverDescription",
                    )}
                    disabled={submitting}
                    rows="4"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: formErrors.short_description
                        ? "1px solid #dc3545"
                        : "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  />
                  {formErrors.short_description && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formErrors.short_description}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    {t("didacticsManager.uploadAudio")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    style={{
                      border: "1px dashed #d9d9d9",
                      padding: "16px",
                      borderRadius: "4px",
                    }}
                  >
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={handleVoiceSelect}
                      disabled={submitting || uploadingVoice}
                    />
                    {voiceFile && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "13px",
                          color: "#666",
                        }}
                      >
                        {voiceFile.name} (
                        {(voiceFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </div>
                    )}
                    {uploadingVoice && (
                      <div style={{ marginTop: "8px", color: "#1890ff" }}>
                        {t("didacticsManager.uploading")}
                      </div>
                    )}
                    {formData.voice_url && !uploadingVoice && (
                      <div style={{ marginTop: "8px", color: "#52c41a" }}>
                        ✓ {t("didacticsManager.uploaded")}
                      </div>
                    )}
                  </div>
                  {formErrors.voice_url && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formErrors.voice_url}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    {t("didacticsManager.duration")}
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder={t("didacticsManager.durationPlaceholder")}
                    disabled={submitting}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: formErrors.duration
                        ? "1px solid #dc3545"
                        : "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  />
                  {formErrors.duration && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formErrors.duration}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Status */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                {t("common.status")}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={submitting}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                }}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && contentToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setContentToDelete(null);
          }}
          title={t("didacticsManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? t("common.deleting") : t("common.delete")}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3>{t("didacticsManager.confirmDeleteMessage")}</h3>
            <div className="item-details">
              <p>
                <strong>{t("didacticsManager.title")}:</strong>{" "}
                {contentToDelete.title}
              </p>
              <p>
                <strong>{t("didacticsManager.category")}:</strong>{" "}
                {getCategoryDisplay(contentToDelete.category)}
              </p>
              <p>
                <strong>{t("didacticsManager.type")}:</strong>{" "}
                {getContentTypeDisplay(contentToDelete.content_type)}
              </p>
              <p>
                <strong>{t("common.createdAt")}:</strong>{" "}
                {formatDate(contentToDelete.created_at)}
              </p>
              <p>
                <strong>{t("common.status")}:</strong>{" "}
                {contentToDelete.status === "ACTIVE"
                  ? t("common.active")
                  : t("common.inactive")}
              </p>
            </div>
            <p className="warning-text">
              {t("didacticsManager.deleteWarning")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default DidacticsPedagogyContentManager;
